import Layout from '@/layouts/index'
import Page from '@/components/Page'
import pageService, {
  GlobalSetting,
  SeoMetadata,
  getAvailablePromptsForChromeExtensionWithPromptDetail,
  settingService,
} from '@/services/page'
import pathService from '@/services/path'
import type { GetStaticPaths, GetStaticProps } from 'next'
import { APPS } from '@/appConfig'
import type { ParsedUrlQuery } from 'querystring'
import {
  useTheme,
  Container,
  Card,
  Grid,
  Typography,
  Button,
  Chip,
  Autocomplete,
  TextField,
  InputAdornment,
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@mui/material'
import { RootStyle } from '@/sections/landing/Shares'
import { notEmpty } from '@/utils/common'
import flatten from 'lodash/flatten'
import { VisitorChromeExtensionPromptResponseWithPromptDetailFragment } from '@/generated/graphql.prompts.node'
import find from 'lodash/find'
import Iconify from '@/components/Iconify'
import { fNumber } from '@/utils/formatNumber'
import CopyClipboard from '@/components/CopyClipboard'
import {
  decodeID,
  formatPrompt,
  languageTransformer,
  urlQueryParamsForChromeExtensionDetailPage,
} from '@/services/prompts/utils'
import { useRouter } from 'next/router'
import {
  PromptSupportingLanguageMap,
  ResponseTone,
  WritingStyle,
  useVisitorFetchPromptUsagesQuery,
  useVisitorGeneratePromptLazyQuery,
} from '@/generated/graphql'
import { useState } from 'react'
import { $enum } from 'ts-enum-util'
import useResponsive from '@/hooks/useResponsive'
import { LoadingButton } from '@mui/lab'
import moment from 'moment'
import SkeletonImageItem from '@/components/skeleton/SkeletonImage'

interface PathProps extends ParsedUrlQuery {
  appId: string
  id: string
}

interface IndexProps {
  title?: string
  globalSetting?: GlobalSetting
  seoMetadataItemsCollection?: SeoMetadata[]
  prompt?: VisitorChromeExtensionPromptResponseWithPromptDetailFragment
  // midjourneyImage?: MidjourneyImagesFragment | null
}

PromptsDetailPage.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout variant="main">{page}</Layout>
}

export default function PromptsDetailPage({
  title,
  globalSetting,
  seoMetadataItemsCollection,
  prompt,
}: IndexProps) {
  const isMobile = useResponsive('down', 'sm')
  const router = useRouter()
  const searchString: urlQueryParamsForChromeExtensionDetailPage = {
    language:
      (router.query.language as PromptSupportingLanguageMap | undefined) ??
      PromptSupportingLanguageMap.English,
    tone: (router.query.tone as ResponseTone | undefined) ?? '',
    writingStyle: (router.query.writingStyle as WritingStyle | undefined) ?? '',
    prompt: (router.query.prompt as string | undefined) ?? '',
  }
  const [textInput, setTextInput] = useState(searchString.prompt)
  const [tone, setTone] = useState<ResponseTone | string>(searchString.tone ?? '')
  const [writingStyle, setWritingStyle] = useState<WritingStyle | string>(
    searchString.writingStyle ?? ''
  )
  const [selectLanguage, setSelectLanguage] = useState<PromptSupportingLanguageMap>(
    searchString.language
  )
  const [visitorGeneratePromptLazyQuery, { loading: visitorGeneratePromptLazyQueryLoading }] =
    useVisitorGeneratePromptLazyQuery()

  const {
    data: visitorFetchPromptUsagesQuery,
    loading: visitorFetchPromptUsagesLoading,
    refetch: visitorFetchPromptUsagesQueryRefetch,
  } = useVisitorFetchPromptUsagesQuery({
    variables: {
      where: prompt
        ? {
            promptId: {
              _like: `%${decodeID(prompt.id)}%`,
            },
          }
        : {},
    },
  })

  const theme = useTheme()
  if (!prompt) {
    return (
      <Page title={title ?? ''} metadataItems={seoMetadataItemsCollection}>
        <RootStyle theme={theme} backgroundColor="">
          <Container>Not Found</Container>
        </RootStyle>
      </Page>
    )
  }

  const items: {
    label: string
    value: number | string
    icon: string
  }[] = [
    {
      label: 'Category',
      value: prompt.category,
      icon: 'material-symbols:category',
    },
    {
      label: 'Views',
      value: prompt.statistics.views,
      icon: 'ic:round-remove-red-eye',
    },
    {
      label: 'Usages',
      value: prompt.statistics.usages,
      icon: 'material-symbols:chat-bubble',
    },
    {
      label: 'Likes',
      value: prompt.statistics.votes,
      icon: 'mdi:like',
    },
  ]

  const sharedCenterSxProps = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  }

  const finalPromptString = formatPrompt({
    language: selectLanguage,
    promptString: prompt.prompt,
    userPrompt: textInput,
    writeStyle: writingStyle,
    responseTone: tone,
  })

  const promptUsages = visitorFetchPromptUsagesQuery?.promptUsages ?? []

  return (
    <Page title={title ?? ''} metadataItems={seoMetadataItemsCollection}>
      <RootStyle theme={theme} backgroundColor="">
        <Container>
          <Card sx={{ mt: 5, p: 5 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h3">{prompt.title}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6">{prompt.promptTeaser}</Typography>
              </Grid>
              {items.map((item) => (
                <Grid key={item.label} item xs={12}>
                  <Chip
                    size="medium"
                    label={
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Iconify
                          sx={{
                            height: 15,
                            width: 15,
                            mr: 0.5,
                          }}
                          icon={item.icon}
                        />
                        <Typography variant="caption">
                          {typeof item.value === 'number' ? fNumber(item.value) : item.value}
                        </Typography>
                      </div>
                    }
                  />
                </Grid>
              ))}
              <Grid item xs={12}>
                <Autocomplete
                  disablePortal
                  id="language"
                  value={languageTransformer(selectLanguage)}
                  options={$enum(PromptSupportingLanguageMap)
                    .getValues()
                    .map((language) => languageTransformer(language))}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Output Language"
                      helperText={`Currently Supporting ${
                        $enum(PromptSupportingLanguageMap).getValues().length
                      } languages`}
                    />
                  )}
                  onChange={(_event, values) => {
                    setSelectLanguage(
                      values?.id ??
                        (PromptSupportingLanguageMap.English as PromptSupportingLanguageMap)
                    )
                  }}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Prompt"
                  helperText={
                    <>
                      <Typography variant="body2">
                        Enter your prompt here, it'll be used as a prompt for Open AI
                      </Typography>
                      <Typography variant="body2">Prompt Hint: {prompt.promptHint}</Typography>
                    </>
                  }
                  value={textInput}
                  onChange={(e) => {
                    setTextInput(e.target.value)
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setTextInput('')
                        }}
                      >
                        <Iconify icon={'system-uicons:reset'} />
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Accordion>
                  <AccordionSummary
                    expandIcon={
                      <Iconify icon={'eva:arrow-ios-downward-fill'} width={20} height={20} />
                    }
                    aria-controls="panel2a-content"
                    id="panel2a-header"
                  >
                    <Typography>Click me to see advance options (Premium Only)</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={4}>
                      <Grid item xs={12}>
                        <Autocomplete
                          disablePortal
                          freeSolo
                          id="Tone"
                          inputValue={tone}
                          options={$enum(ResponseTone).getValues()}
                          fullWidth
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Tone you prefer"
                              helperText="Choose a tone for the response you prefer"
                            />
                          )}
                          onInputChange={(_event, values) => setTone(values)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Autocomplete
                          disablePortal
                          freeSolo
                          id="WritingStyle"
                          inputValue={writingStyle}
                          options={$enum(WritingStyle).getValues()}
                          fullWidth
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Writing Style"
                              helperText="Choose a writing style for the response you prefer"
                            />
                          )}
                          onInputChange={(_event, values) => setWritingStyle(values)}
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              <Grid item xs={12}>
                {textInput && <CopyClipboard key={textInput} shareInputValue={finalPromptString} />}
              </Grid>

              <Grid item xs={12}>
                {textInput && (
                  <Grid container>
                    <Grid item xs={12} sx={sharedCenterSxProps}>
                      <Button
                        target="_blank"
                        variant={isMobile ? 'text' : 'outlined'}
                        fullWidth
                        href={'https://chat.openai.com/'}
                      >
                        Go to Open AI Page
                        <Iconify
                          sx={{ ml: 1 }}
                          icon={'material-symbols:arrow-right'}
                          width={25}
                          height={25}
                        />
                      </Button>
                    </Grid>
                  </Grid>
                )}
              </Grid>
              <Grid item xs={12}>
                {textInput && (
                  <LoadingButton
                    variant="contained"
                    fullWidth
                    loading={visitorGeneratePromptLazyQueryLoading}
                    onClick={() => {
                      visitorGeneratePromptLazyQuery({
                        variables: {
                          promptId: decodeID(prompt.id),
                          language: selectLanguage,
                          prompt: textInput,
                        },
                        notifyOnNetworkStatusChange: true,
                        fetchPolicy: 'no-cache',
                        onCompleted: (data) => {
                          visitorFetchPromptUsagesQueryRefetch()

                          console.log('data', data.generatePrompt.createResponse?.choices)
                        },
                      })
                    }}
                  >
                    Magic
                  </LoadingButton>
                )}
              </Grid>
            </Grid>
          </Card>
          <Grid container spacing={3} sx={{ mt: 6 }}>
            <Grid item xs={12}>
              <Typography variant="h6">User Input & Output</Typography>
            </Grid>
            {visitorFetchPromptUsagesLoading ? (
              <SkeletonImageItem />
            ) : promptUsages.length === 0 ? (
              <Grid item xs={12}>
                <Typography variant="body2">No Record Found</Typography>
              </Grid>
            ) : (
              visitorFetchPromptUsagesQuery?.promptUsages.map((promptUsage) => {
                const response = promptUsage.response
                const output = (response?.choices?.[0]?.text ?? '').trim()
                return (
                  <Grid item xs={12} md={6} key={promptUsage.id}>
                    <Card sx={{ mt: 5, p: 5 }}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Typography variant="h6">User Input</Typography>
                          <CopyClipboard shareInputValue={promptUsage.userPrompt} />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="h6">User Output</Typography>
                          <CopyClipboard shareInputValue={output} />
                        </Grid>
                        <Grid item xs={12}>
                          <Chip
                            size="medium"
                            label={`Created ${moment(promptUsage.createdAt).fromNow()}`}
                          />
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                )
              })
            )}
          </Grid>
        </Container>
      </RootStyle>
    </Page>
  )
}

export const getStaticPaths: GetStaticPaths<PathProps> = async () => {
  const { getImageCollectionPaths } = pathService()

  const paths = await Promise.allSettled(
    APPS.map(async (app) => getImageCollectionPaths(app.id))
  ).then((res) => res.map((r) => (r.status === 'fulfilled' ? r.value : null)).filter(notEmpty))
  const finalPath = flatten(paths)
  return {
    paths: finalPath,
    fallback: true,
  }
}

export const getStaticProps: GetStaticProps<IndexProps, PathProps> = async ({ params, locale }) => {
  if (!params) throw new Error('No path parameters found')

  const { appId } = params
  const { getPage } = pageService(appId)
  const { getGlobalSetting } = settingService(appId)
  const [{ title, seoMetadataItemsCollection }, globalSetting, data = []] = await Promise.all([
    getPage('/', { locale }),
    getGlobalSetting({ locale }),
    getAvailablePromptsForChromeExtensionWithPromptDetail(appId),
  ])
  const prompt = find(data, { id: params.id })

  return {
    props: {
      prompt,
      title: title ?? '',
      globalSetting,
      seoMetadataItemsCollection,
    },
    revalidate: 3600,
  }
}
